import { buildPermissionChecker, PermissionChecker, RoleLike } from '@app/hooks/usePermissions';
import { HomeDashboard, PRIVILEGED_ROLES, resolveHomeDashboard } from '@app/components/router/homeDashboard';
import { SidebarNavigationItem } from './sidebarNavigation';

export const HOME_NAV_KEY = 'nft-dashboard';
const ADMIN_ONLY_KEYS = new Set(['generals', 'users', 'roles', 'permissions']);
const PAYROLL_ONLY_KEYS = new Set(['salaries', 'bonus']);
const NO_ACCESS_PATH = '/403';

export interface NavAccess {
  can: PermissionChecker['can'];
  hasGrant: PermissionChecker['hasGrant'];
  isSuperUser: boolean;
  canViewAdminSettings: boolean;
  canViewSalaryList: boolean;
  /** Dashboard trang chủ user được xem; `null` = không có quyền trang Tổng quan. */
  home: HomeDashboard | null;
  /** Vai trò `sales`: backend cho xem dashboard bán hàng và bonus mà không cần permission. */
  isSales: boolean;
  /** Tài khoản có liên kết nhân viên — điều kiện cho các trang "của tôi". */
  hasEmployee: boolean;
  /** Messaging::ConversationPolicy: admin/manager hoặc quyền cấp riêng (không gồm accountant). */
  canViewInbox: boolean;
  canManageMessagingChannels: boolean;
  canManageAutoReplies: boolean;
}

/** Phần của UserModel (localStorage) cần để tính quyền menu. */
interface NavUser {
  employee?: { id: number | null } | null;
  sales_employee?: boolean | number | null;
  roles?: RoleLike[] | null;
}

export const buildNavAccess = (user: NavUser | null | undefined): NavAccess => {
  const roleNames = (user?.roles ?? []).map((role) => String(role.name ?? '').toLowerCase());
  const { can, hasGrant, isSuperUser } = buildPermissionChecker(user?.roles);
  const employeeId = user?.employee?.id ?? (user?.sales_employee ? Number(user.sales_employee) : null);

  return {
    can,
    hasGrant,
    isSuperUser,
    canViewAdminSettings: roleNames.includes('admin'),
    canViewSalaryList: roleNames.some((name) => PRIVILEGED_ROLES.includes(name)),
    home: resolveHomeDashboard(roleNames, employeeId, can('reports/dashboard', 'index')),
    isSales: roleNames.includes('sales'),
    hasEmployee: Boolean(employeeId),
    canViewInbox:
      roleNames.includes('admin') || roleNames.includes('manager') || hasGrant('messaging/conversations', 'index'),
    canManageMessagingChannels: hasGrant('messaging/channels', 'manage'),
    canManageAutoReplies:
      roleNames.includes('admin') || roleNames.includes('manager') || hasGrant('messaging/auto_reply_rules', 'manage'),
  };
};

// Trang "của tôi" — khớp policy backend tương ứng.
/** Reports::SalesDashboardPolicy: vai trò sales (cần nhân viên để lọc số liệu). */
export const canViewMyReport = (access: NavAccess): boolean => access.hasEmployee && access.isSales;
/** Employee::SalaryPolicy#index?: quyền index hoặc view_own. */
export const canViewMySalary = (access: NavAccess): boolean =>
  access.hasEmployee && access.can('employee/salaries', 'index');
/** BonusPolicy#index?: vai trò sales, hoặc quyền index / view_own. */
export const canViewMyBonus = (access: NavAccess): boolean =>
  access.hasEmployee && (access.isSales || access.can('bonuses', 'index'));

const KEY_RULES: Record<string, (access: NavAccess) => boolean> = {
  [HOME_NAV_KEY]: (access) => access.home !== null,
  // UserSessionPolicy / AuditEventPolicy: admin hoặc quyền cấp riêng, không mặc định cho manager/accountant.
  sessions: (access) => access.hasGrant('user_sessions', 'index'),
  'audit-log': (access) => access.hasGrant('audit_events', 'index'),
  inbox: (access) => access.canViewInbox,
  'messaging-channels': (access) => access.canManageMessagingChannels,
  'messaging-auto-replies': (access) => access.canManageAutoReplies,
  timesheets: (access) => access.can('employee/timesheets', 'index') || access.can('employee/salaries', 'update'),
  // Nhân viên gửi đơn của mình; quản lý/kế toán không gắn nhân viên vẫn cần xem danh sách.
  'leave-requests': (access) => access.hasEmployee || access.isSuperUser,
  'leave-calendar': (access) => access.hasEmployee || access.isSuperUser,
  'my-report': canViewMyReport,
  'my-salary': canViewMySalary,
  'my-bonus': canViewMyBonus,
};

/** URL trong sidebarNavigation có chỗ có "/" đầu, chỗ không → chuẩn hoá để so với pathname. */
export const toPath = (url: string): string => `/${url.replace(/^\//, '')}`;

const canSeeItem = (item: SidebarNavigationItem, access: NavAccess): boolean => {
  if (item.public) return true;
  const rule = KEY_RULES[item.key];
  if (rule) return rule(access);
  if (ADMIN_ONLY_KEYS.has(item.key)) return access.canViewAdminSettings;
  if (PAYROLL_ONLY_KEYS.has(item.key)) return access.canViewSalaryList;
  // Chưa khai báo quyền thì chỉ super user thấy — mặc định an toàn là ẩn.
  if (!item.permissionKey) return access.isSuperUser;

  const [controller, action = 'index'] = item.permissionKey.split(':');
  return access.can(controller, action);
};

/** Chỉ giữ menu user được phép; nhóm không còn mục con nào thì ẩn luôn. */
export const filterNavigation = (nav: SidebarNavigationItem[], access: NavAccess): SidebarNavigationItem[] =>
  nav.reduce<SidebarNavigationItem[]>((result, item) => {
    if (item.children) {
      const children = item.children.filter((child) => canSeeItem(child, access));
      return children.length > 0 ? [...result, { ...item, children }] : result;
    }
    return canSeeItem(item, access) ? [...result, item] : result;
  }, []);

/**
 * Trang đích sau đăng nhập: Tổng quan nếu được xem, ngược lại menu đầu tiên được phép
 * theo thứ tự sidebar (ưu tiên trang danh sách hơn trang `landing: false` như "Tạo đơn hàng").
 */
export const resolveLandingPath = (nav: SidebarNavigationItem[], access: NavAccess): string => {
  if (access.home) return '/';

  const leaves = filterNavigation(nav, access)
    .flatMap((item) => item.children ?? [item])
    .filter((item) => item.url && !item.public);
  const target = leaves.find((item) => item.landing !== false) ?? leaves[0];

  return target?.url ? toPath(target.url) : NO_ACCESS_PATH;
};
